/**
 * Feature Gated Route Component
 *
 * Wraps routes that should only be accessible based on UI mode.
 * Product-level gating is now based on licenses (useHasLicense),
 * not on tenant feature flags.
 */

import React from 'react';
import { Navigate, useLocation } from 'react-router';
import { Routes } from 'enums';
import { useTenantFeatures } from 'hooks/use-tenant-features';
import { Box, CircularProgress } from '@mui/material';

interface FeatureGatedRouteProps {
  children: React.ReactNode;
  /**
   * If true, only accessible in full mode (not simplified)
   */
  requireFullMode?: boolean;
  /**
   * Fallback route if mode requirements aren't met
   */
  fallbackRoute?: string;
}

/**
 * Wraps a route to check if the UI mode requirements are met
 */
const FeatureGatedRoute: React.FC<FeatureGatedRouteProps> = ({
  children,
  requireFullMode = false,
  fallbackRoute,
}) => {
  const location = useLocation();
  const { isFullMode, isLoading, getLandingPage } = useTenantFeatures();

  // Show loading while checking permissions
  if (isLoading) {
    return (
      <Box
        sx={{
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          height: '100vh',
        }}
      >
        <CircularProgress />
      </Box>
    );
  }

  // Check if full mode is required
  if (requireFullMode && !isFullMode) {
    const redirectTo = fallbackRoute || getLandingPage();
    return <Navigate to={redirectTo} state={{ from: location }} replace />;
  }

  return <>{children}</>;
};

/**
 * Simplified Mode Only Route
 * Only accessible when tenant is in simplified mode
 */
interface SimplifiedModeRouteProps {
  children: React.ReactNode;
  fallbackRoute?: string;
}

export const SimplifiedModeRoute: React.FC<SimplifiedModeRouteProps> = ({
  children,
  fallbackRoute = Routes.AI_CHAT,
}) => {
  const location = useLocation();
  const { isSimplifiedMode, isLoading } = useTenantFeatures();

  if (isLoading) {
    return (
      <Box
        sx={{
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          height: '100vh',
        }}
      >
        <CircularProgress />
      </Box>
    );
  }

  // Redirect to fallback if not in simplified mode
  if (!isSimplifiedMode) {
    return <Navigate to={fallbackRoute} state={{ from: location }} replace />;
  }

  return <>{children}</>;
};

/**
 * Dynamic Landing Redirect
 * Redirects to appropriate landing page based on tenant mode
 */
export const DynamicLandingRedirect: React.FC = () => {
  const { getLandingPage, isLoading } = useTenantFeatures();

  if (isLoading) {
    return (
      <Box
        sx={{
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          height: '100vh',
        }}
      >
        <CircularProgress />
      </Box>
    );
  }

  return <Navigate to={getLandingPage()} replace />;
};

export default FeatureGatedRoute;
